import { useEffect, useRef, useState } from 'react';

interface VoiceSamplePlayerProps {
  url?: string | null;
  disabled?: boolean;
}

// Inline SVG rather than Font Awesome `<i>` icons: the Font Awesome kit script swaps an
// `<i>` for an `<svg>` once, so changing its class afterwards changes nothing visible.
const ICON_PATHS = {
  play: 'M8 5v14l11-7z',
  stop: 'M6 6h12v12H6z',
  failed: 'M12 2 1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2V9h2v4z',
};

// Plays the sample of the selected voice. Changing the voice stops the old sample.
function VoiceSamplePlayer({ url, disabled = false }: VoiceSamplePlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // True from the click until the sample ends or is stopped, so the button turns
  // into "stop" at once instead of after the sample has been fetched.
  const [active, setActive] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setActive(false);
    setFailed(false);
  }, [url]);

  useEffect(
    () => () => {
      audioRef.current?.pause();
    },
    [],
  );

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (active) {
      audio.pause();
      audio.currentTime = 0;
      setActive(false);
      return;
    }

    setFailed(false);
    setActive(true);
    audio.play().catch((error: unknown) => {
      // Stopping while the sample is still loading aborts play(); that is not a failure.
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setActive(false);
      setFailed(true);
    });
  };

  const unavailable = !url;
  const title = unavailable
    ? 'This voice has no sample'
    : failed
      ? 'Failed to play the sample'
      : active
        ? 'Stop sample'
        : 'Play sample';

  return (
    <>
      <button
        type="button"
        className="btn btn-secondary btn-small"
        onClick={toggle}
        disabled={disabled || unavailable}
        title={title}
        aria-label={title}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d={ICON_PATHS[failed ? 'failed' : active ? 'stop' : 'play']} />
        </svg>
      </button>
      <audio
        ref={audioRef}
        src={url ?? undefined}
        preload="none"
        onEnded={() => setActive(false)}
        onPause={() => setActive(false)}
        onError={() => {
          setActive(false);
          if (url) setFailed(true);
        }}
      />
    </>
  );
}

export default VoiceSamplePlayer;
